import { useState } from 'react';
import { FiCheckCircle, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { Box, Cartao, Etiqueta, Flex, Vazio } from './base';
import { agrupar, CLASSES, dataCurta, estadoDoAlerta, GrupoDeAlertas, plural } from './formato';
import { Alerta, AlertasPorClasse, Classe, Resolvido } from './tipos';

// "Pede sua ação": os alertas de UMA visão numa lista curta, do mais grave ao
// menos. Antes eram duas peças (a lista de alertas com filtro e etiquetas, e
// "Por onde começar" ao lado, com as mesmas coisas reescritas). Agora cada
// linha diz só o que é e o que fazer; natureza, período, justificativa e
// histórico ficam na gaveta, a um clique. Os primeiros LIMITE aparecem; o resto
// abre em "ver todos".

const LIMITE = 5;

const ROTULO_CURTO: Record<Classe, [string, string]> = {
  critico: ['crítico', 'críticos'],
  atencao: ['de atenção', 'de atenção'],
  oportunidade: ['oportunidade', 'oportunidades'],
  informativo: ['informativo', 'informativos'],
};

const nomeDaClasse = (c: Classe) => CLASSES.find(([k]) => k === c)?.[1] || c;

// A faixa de cor da gravidade, à esquerda da linha.
function Faixa({ classe }: { classe: Classe }) {
  return (
    <Box
      as="span"
      role="img"
      aria-label={nomeDaClasse(classe)}
      alignSelf="stretch"
      w="4px"
      minH="20px"
      flexShrink={0}
      borderRadius="2px"
      bg={`var(--pa-${classe})`}
    />
  );
}

const LINHA = {
  display: 'flex',
  alignItems: 'center',
  sx: { gap: '12px' },
  w: '100%',
  textAlign: 'left',
  pr: '12px',
  py: '12px',
  bg: 'transparent',
  border: 'none',
  borderTop: '1px solid var(--pa-grade)',
  color: 'var(--pa-tinta)',
  cursor: 'pointer',
  _hover: { bg: 'var(--pa-superficie-2)' },
  _focusVisible: { outline: '2px solid var(--pa-dado)', outlineOffset: '-2px' },
};

// A linha: a gravidade, o título e, embaixo, a ação.
function Linha({ alerta, onAbrir, recuo }: { alerta: Alerta; onAbrir: (a: Alerta) => void; recuo?: boolean }) {
  const estado = estadoDoAlerta(alerta.acompanhamento);
  // Só o que muda a leitura: novo ou reaberto. "Persiste desde" fica na gaveta.
  const marca = estado && estado.tom !== 'persistente' ? estado : null;
  return (
    <Box as="button" type="button" onClick={() => onAbrir(alerta)} pl={recuo ? '28px' : '16px'} {...LINHA}>
      <Faixa classe={alerta.classe} />
      <Box flex="1" minW={0}>
        <Box fontSize="14px" lineHeight="20px" fontWeight={600}>
          {alerta.titulo}
        </Box>
        {alerta.acao && (
          <Box fontSize="13px" lineHeight="18px" color="var(--pa-tinta-3)" mt="2px" noOfLines={1}>
            {alerta.acao}
          </Box>
        )}
      </Box>
      {marca && (
        <Box as="span" title={marca.detalhe} flexShrink={0}>
          <Etiqueta tom={marca.tom}>{marca.rotulo}</Etiqueta>
        </Box>
      )}
      <Box as="span" color="var(--pa-tinta-3)" aria-hidden="true" flexShrink={0}>
        <FiChevronRight size={18} />
      </Box>
    </Box>
  );
}

// O mesmo indicador repetido (oito vendedores em queda) vira uma linha que abre.
function Grupo({ grupo, onAbrir }: { grupo: GrupoDeAlertas; onAbrir: (a: Alerta) => void }) {
  const [aberto, setAberto] = useState(false);
  if (grupo.alertas.length === 1) return <Linha alerta={grupo.alertas[0]} onAbrir={onAbrir} />;
  return (
    <Box>
      <Box as="button" type="button" aria-expanded={aberto} onClick={() => setAberto(!aberto)} pl="16px" {...LINHA}>
        <Faixa classe={grupo.classe} />
        <Box flex="1" minW={0}>
          <Box fontSize="14px" lineHeight="20px" fontWeight={600}>
            {grupo.alertas.length} alertas: {grupo.indicador}
          </Box>
          <Box fontSize="13px" lineHeight="18px" color="var(--pa-tinta-3)" mt="2px" noOfLines={1}>
            o maior: {grupo.alertas[0].titulo}
          </Box>
        </Box>
        <Box as="span" color="var(--pa-tinta-3)" flexShrink={0} transform={aberto ? 'rotate(180deg)' : undefined} transition="transform .15s">
          <FiChevronDown size={18} aria-hidden="true" />
        </Box>
      </Box>
      {aberto && grupo.alertas.map((a) => <Linha key={a.chave} alerta={a} onAbrir={onAbrir} recuo />)}
    </Box>
  );
}

export function BotaoDoRodape({ children, ...props }: any) {
  return (
    <Box
      as="button"
      type="button"
      display="inline-flex"
      alignItems="center"
      sx={{ gap: '6px' }}
      p="4px 0"
      bg="transparent"
      border="none"
      textAlign="left"
      fontSize="13px"
      fontWeight={600}
      color="var(--pa-marca-tinta)"
      cursor="pointer"
      _hover={{ textDecoration: 'underline' }}
      _focusVisible={{ outline: '2px solid var(--pa-dado)', outlineOffset: '2px' }}
      {...props}
    >
      {children}
    </Box>
  );
}

export function PedeAcao({
  titulo,
  alertas,
  fora,
  resolvidos,
  vazio,
  onAbrir,
  rodape,
  ...props
}: {
  titulo: string;
  alertas?: AlertasPorClasse;
  fora?: number;
  resolvidos?: Resolvido[];
  vazio: string;
  onAbrir: (a: Alerta) => void;
  rodape?: React.ReactNode;
  [k: string]: any;
}) {
  const [todos, setTodos] = useState(false);
  const [verResolvidos, setVerResolvidos] = useState(false);
  const lista = CLASSES.flatMap(([c]) => alertas?.[c] || []);
  const grupos = agrupar(lista);
  // "Ver todos os 16 alertas" mostra os 16, um por linha: antes abria só os
  // grupos, fechados, e o dono via menos linhas que o prometido (04/11/2026).
  const visiveis = todos ? agrupar(lista, Infinity) : grupos.slice(0, LIMITE);
  // Há alerta escondido além do limite ou dentro de um grupo fechado.
  const escondidos = lista.length > Math.min(grupos.length, LIMITE);
  const graves = (['critico', 'atencao'] as Classe[]).filter((c) => alertas?.[c]?.length);
  const temRodape = escondidos || !!fora || !!resolvidos?.length || !!rodape;

  return (
    <Cartao as="section" aria-label={titulo} overflow="hidden" display="flex" flexDirection="column" {...props}>
      <Flex align="baseline" justify="space-between" wrap="wrap" sx={{ gap: '4px 12px' }} px="16px" pt="14px" pb="12px">
        <Box as="h3" m={0} fontSize="16px" lineHeight="22px" fontWeight={700}>
          {titulo}
        </Box>
        {graves.length > 0 && (
          <Box fontSize="13px">
            {graves.map((c, i) => (
              <Box as="span" key={c} color={`var(--pa-${c})`} fontWeight={600}>
                {i > 0 && (
                  <Box as="span" color="var(--pa-tinta-3)" fontWeight={400}>
                    {' · '}
                  </Box>
                )}
                {plural(alertas![c].length, ...ROTULO_CURTO[c])}
              </Box>
            ))}
          </Box>
        )}
      </Flex>
      {lista.length === 0 ? (
        <Box px="16px" pb="16px">
          <Vazio>{vazio}</Vazio>
        </Box>
      ) : (
        visiveis.map((g) => <Grupo key={g.chave} grupo={g} onAbrir={onAbrir} />)
      )}
      {temRodape && (
        <Flex direction="column" align="flex-start" sx={{ gap: '4px' }} px="16px" py="10px" borderTop="1px solid var(--pa-grade)" mt="auto">
          {escondidos && (
            <BotaoDoRodape aria-expanded={todos} onClick={() => setTodos(!todos)}>
              {todos ? 'Ver menos' : `Ver todos os ${lista.length} alertas`}
            </BotaoDoRodape>
          )}
          {!!fora && (todos || !escondidos) && (
            <Box fontSize="12px" color="var(--pa-tinta-3)">
              e mais {fora} alertas menores fora da lista
            </Box>
          )}
          {!!resolvidos?.length && (
            <>
              <BotaoDoRodape aria-expanded={verResolvidos} color="var(--pa-sobe)" onClick={() => setVerResolvidos(!verResolvidos)}>
                <FiCheckCircle size={14} aria-hidden="true" />
                {plural(resolvidos.length, 'resolvido', 'resolvidos')} nos últimos 7 dias
              </BotaoDoRodape>
              {verResolvidos && (
                <Box as="ul" listStyleType="none" m={0} p={0} display="flex" flexDirection="column" sx={{ gap: '6px' }}>
                  {resolvidos.map((r) => (
                    <Box as="li" key={`${r.titulo}-${r.resolvido_em}`} fontSize="12px" lineHeight="17px">
                      <Box color="var(--pa-tinta)">{r.titulo}</Box>
                      <Box color="var(--pa-tinta-3)">
                        de {dataCurta(r.desde)} a {dataCurta(r.resolvido_em)}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
          {rodape}
        </Flex>
      )}
    </Cartao>
  );
}
